import { computed, watch } from 'vue';
import { useUserStore } from '@/stores/user';
import { useSnackbar } from '@/composables/useSnackbar';
import i18next from '@/config/i18n';
import axios from '@/plugins/axios';

// invalid = orphan-like (upload never completed) but inside a batch — retryable,
// not actively running, so it counts toward allTerminal.
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'abandoned', 'invalid']);
const RETRYABLE_STATUSES = new Set(['failed', 'invalid']);
// Statuses that DELETE /jobs/{id} accepts. Everything else (active jobs +
// orphan-like invalid) must go through the experiment cancel route (estorno).
const DISMISSIBLE_STATUSES = new Set(['completed', 'failed', 'abandoned']);

const tk = (key, params) => i18next.t(`components.common.queueStatus.${key}`, params);

export function useBatchQueue() {
    const userStore = useUserStore();
    const { showSnackbar } = useSnackbar();

    const batchedEntries = computed(() => {
        const batchMap = new Map();
        for (const job of userStore.processingQueue) {
            if (!job.batch_id) continue;
            if (!batchMap.has(job.batch_id)) batchMap.set(job.batch_id, []);
            batchMap.get(job.batch_id).push(job);
        }
        return Array.from(batchMap.entries()).map(([batchId, jobs]) => {
            const completedCount = jobs.filter(j => j.status === 'completed').length;
            const failedCount = jobs.filter(j => RETRYABLE_STATUSES.has(j.status)).length;
            // SYN-962: 'abandoned' = cancelled/orphaned (não é sucesso nem falha
            // retriável). Contado à parte pra sinalizar no sino em vez de sumir
            // sob um check verde de "0 com falha".
            const cancelledCount = jobs.filter(j => j.status === 'abandoned').length;
            const activeCount = jobs.filter(j => !TERMINAL_STATUSES.has(j.status)).length;
            const allTerminal = activeCount === 0;
            const earliestMs = jobs.reduce((min, j) => {
                const ms = j.created_at ? new Date(j.created_at).getTime() : Infinity;
                return ms < min ? ms : min;
            }, Infinity);
            return {
                batchId,
                jobs,
                completedCount,
                failedCount,
                cancelledCount,
                activeCount,
                allTerminal,
                created_at: Number.isFinite(earliestMs) ? new Date(earliestMs).toISOString() : null,
            };
        });
    });

    // Track batches seen in non-terminal state this session so we only toast
    // when a batch transitions from active → all-terminal (not on F5 of already-done batches).
    const _seenActive = new Set();
    const _announced = new Set();

    const stopWatch = watch(batchedEntries, (entries) => {
        for (const entry of entries) {
            if (!entry.allTerminal) {
                _seenActive.add(entry.batchId);
            } else if (_seenActive.has(entry.batchId) && !_announced.has(entry.batchId)) {
                _announced.add(entry.batchId);
                // SYN-962: falha → warning; nada concluído nem falho (só
                // cancelados) → info neutro, nunca 'success' verde; senão success.
                const type = entry.failedCount > 0
                    ? 'warning'
                    : entry.completedCount === 0 ? 'info' : 'success';
                let message = tk('batch.completed', { ok: entry.completedCount, failed: entry.failedCount });
                if (entry.cancelledCount > 0) {
                    message += tk('batch.cancelledSuffix', { cancelled: entry.cancelledCount });
                }
                showSnackbar(message, type, 5000);
            }
        }
    });

    // Remove the whole batch notification at once (the X on the batch header),
    // regardless of state, in two steps per job:
    //   1. Anything not already dismissible (active queued/pending/processing +
    //      orphan-like invalid/awaiting_upload) is cancelled first — DELETE
    //      /experiments/{job_id}/cancel abandons it and refunds the credit
    //      (estorno). NB: the path param is the **job id**, not experiment_id,
    //      despite the route name; passing experiment_id returns "Job not found".
    //   2. The now-terminal job is dismissed (DELETE /jobs/{id}) so it leaves the
    //      queue entirely — otherwise abandoned/terminal jobs would linger in the
    //      batch on the next fetch.
    // Each step is best-effort and independent: a job that completed mid-flight
    // (cancel 409) still gets a dismiss attempt; the final fetch reconciles.
    async function dismissBatch(batchId) {
        const entry = batchedEntries.value.find(e => e.batchId === batchId);
        if (!entry) return;
        for (const job of entry.jobs) {
            if (!DISMISSIBLE_STATUSES.has(job.status)) {
                try { await userStore.cancelExperimentJob(job.id); } catch { /* may have finished; try dismiss anyway */ }
            }
            try { await userStore.dismissJob(job.id); } catch { /* still active: leave it, next fetch reconciles */ }
        }
        await userStore.fetchProcessingQueue();
    }

    async function retryBatchFailed(batchId) {
        const entry = batchedEntries.value.find(e => e.batchId === batchId);
        if (!entry) return;
        const retryable = entry.jobs.filter(j => RETRYABLE_STATUSES.has(j.status));
        let successCount = 0;
        let failCount = 0;
        for (const job of retryable) {
            try {
                await axios.post(`/processing/jobs/${job.id}/retry`);
                successCount++;
            } catch {
                failCount++;
            }
        }
        await userStore.fetchProcessingQueue();
        showSnackbar(
            tk('batch.retryResult', { ok: successCount, failed: failCount }),
            failCount > 0 ? 'warning' : 'success',
        );
    }

    return { batchedEntries, retryBatchFailed, dismissBatch, _stopWatch: stopWatch };
}
